import axios from 'axios';
import { DateTime } from 'luxon';
import { isDate, isString, min, max, ceil, uniq, keys, orderBy } from 'lodash';
import { fr as LocalFr } from 'date-fns/locale';
import { format, formatDistanceToNow } from 'date-fns';
import { isFirebaseTimestamp } from './type_check';
// ----------------------------------------------------------------------

export function fDate(date, _format = 'dd MMMM yyyy') {
  const convert = gDate(date);

  if (convert === null) return '';

  return format(convert, _format, {
    locale: LocalFr
  });
}

export function fDateTime(date) {
  return format(new Date(date), 'dd MMM yyyy HH:mm', {
    locale: LocalFr
  });
}

export function fTime(date) {
  return format(new Date(date), 'HH:mm', {
    locale: LocalFr
  });
}

export function fDateTimeSuffix(date) {
  return format(new Date(date), 'dd/MM/yyyy HH:mm');
}

export function fToNow(date) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: LocalFr
  });
}

export function epochToDate(epoch) {
  return DateTime.fromJSDate(epoch.toDate()).toFormat('dd MMM yyyy');
}

export function epochToDateTime(epoch) {
  return DateTime.fromJSDate(epoch.toDate()).toFormat('dd MMM yyyy HH:mm');
}

/**
 * @deprecated use [gfDate] instead
 * @param date
 * @returns {string|*}
 */
export const getDate = (date) => {
  return isFirebaseTimestamp(date) ? fDate(date.toDate()) : fDate(date);
};

/**
 * @deprecated use [gfDateTime] instead
 * @param date
 * @returns {string|*}
 */
export const getDateTime = (date) => {
  return isFirebaseTimestamp(date) ? fDateTime(date.toDate()) : fDateTime(date);
};

/**
 *
 * @param {*} date
 * @returns {Date?}
 */
export const gDate = (date) => {
  if (isFirebaseTimestamp(date)) return date.toDate();
  else if (isDate(date)) return date;
  else if (isString(date)) return new Date(date);
  return null;
};

export const gfDate = (date) => {
  return date ? fDate(gDate(date)) : null;
};

export const gfDateTime = (date) => {
  return date ? fDateTime(gDate(date)) : null;
};

export const gfTime = (date) => {
  return date ? fTime(gDate(date)) : null;
};

export const gfRange = (start, end) => {
  const a = gDate(start);
  const b = gDate(end);

  if (a === null || b === null) return null;
  const _start = DateTime.fromJSDate(a);
  const _end = DateTime.fromJSDate(b);
  const today = DateTime.fromJSDate(new Date());

  const _format = (date, formattext) => {
    return format(gDate(date), formattext, {
      locale: LocalFr
    });
  };

  if (_start.year === _end.year && _start.year === today.year) {
    if (_start.month === _end.month && _start.day === _end.day) {
      return _format(start, 'dd MMMM');
    }
    if (_start.month === _end.month && _start.day !== _end.day) {
      return `${_format(start, 'dd')} - ${_format(end, 'dd MMMM')}`;
    }

    return `${_format(start, 'dd MMMM')} - ${_format(end, 'dd MMMM')}`;
  }

  return `${gfDate(start)} - ${gfDate(end)}`;
};

export const diffDate = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = DateTime.fromJSDate(gDate(startDate));
  const end = DateTime.fromJSDate(gDate(endDate));
  const { days, years, hours, minutes, milliseconds } = end.diff(start, [
    'days',
    'years',
    'hours',
    'minutes',
    'milliseconds'
  ]);

  return { days, years, hours, minutes, milliseconds };
};

export const datesBetween = (startDate, endDate) => {
  const collection = [gDate(startDate), gDate(endDate)].filter((date) => isDate(date));
  const start = min(collection);
  const end = max(collection);
  const days = ceil(diffDate(start, end)?.days || 0);

  const dates = [...Array(days)].map((_, index) => {
    return DateTime.fromJSDate(start).plus({ days: index }).toJSDate();
  });

  return uniq(dates);
};

export const weeksBetween = (startDate, endDate) => {
  const dates = datesBetween(startDate, endDate);

  const weeks = dates.map((date) => {
    return DateTime.fromJSDate(date).weekNumber;
  });

  return uniq(weeks);
};

export const yearsBetween = (startDate, endDate) => {
  const dates = datesBetween(startDate, endDate);

  const years = dates.map((date) => {
    return DateTime.fromJSDate(date).year;
  });

  return uniq(years);
};

export const daysOfWeek = (date = new Date()) => {
  const current = DateTime.fromJSDate(gDate(date));
  const firstDay = current.startOf('week');

  return [...Array(7)].map((_, idx) => {
    return firstDay.plus({ days: idx }).toJSDate();
  });
};

export const onlineTime = async () => {
  try {
    const { data } = await axios.get('http://worldtimeapi.org/api/ip');

    return new Date(data?.utc_datetime);
  } catch (e) {
    console.error(e);
    return new Date();
  }
};

export function getOnlyDate(date) {
  const parsed = gDate(date);

  if (parsed === null) return null;

  const year = parsed.getFullYear();
  const month = parsed.getMonth();
  const day = parsed.getDate();

  return new Date(year, month, day);
}

export function getRappelLikeDate(date) {
  const parsed = gDate(date);

  if (parsed === null) return null;

  const year = parsed.getFullYear();
  const month = parsed.getMonth();
  const day = parsed.getDate();
  const hour = parsed.getHours();
  const minutes = parsed.getMinutes();

  return new Date(year, month, day, hour, minutes);
}

export const isToday = (date) => {
  const today = getOnlyDate(new Date());
  const _date = getOnlyDate(date);

  return today?.toString() === _date?.toString();
};
export const groupByDate = (list = [], dateKey = 'createdAt') => {
  const map = {};

  list.forEach((el) => {
    const cursor = getOnlyDate(el.createdAt);
    const isIncluded = Boolean(keys(map).filter((key) => key.toString() === cursor.toString()).length);

    if (isIncluded) {
      map[cursor] = [...map[cursor], el];
    } else {
      map[cursor] = [el];
    }
  });

  const result = keys(map).map((date) => ({
    date: gDate(date),
    data: map[date]
  }));

  return orderBy(result, dateKey, 'desc');
};

export const getDayInterval = (date = new Date()) => {
  const _date = gDate(date);

  if (_date === null) return null;

  const instance = DateTime.fromJSDate(_date);
  const start = new Date(instance.year, instance.month - 1, instance.day, 0, 0, 0);
  const end = new Date(instance.year, instance.month - 1, instance.day, 23, 59, 0);

  return { start, end };
};

export const getWeekInterval = (date = new Date()) => {
  const _date = gDate(date);

  if (_date === null) return null;

  const instance = DateTime.fromJSDate(_date);
  const start = instance.set({ weekday: 1, hour: 0, minute: 0, second: 0 }).toJSDate();
  const end = instance.set({ weekday: 7, hour: 23, minute: 59, second: 0 }).toJSDate();

  return { start, end };
};

export const getMonthInterval = (date = new Date()) => {
  const _date = gDate(date);

  if (_date === null) return null;

  const instance = DateTime.fromJSDate(_date);
  const start = instance.set({ day: 1, hour: 0, minute: 0, second: 0 }).toJSDate();
  const end = instance
    .set({
      day: 1,
      hour: 0,
      minute: 0,
      second: 0
    })
    .plus({ day: 33 })
    .set({ day: 1 })
    .minus({ day: 1 })
    .toJSDate();

  return { start, end };
};
export const orderDates = (collection = [], desc = false) => {
  return collection
    ?.map((date) => gDate(date))
    ?.sort((a, b) => {
      if (desc) return new Date(a).getTime() < new Date(b).getTime() ? 1 : -1;

      return new Date(a).getTime() > new Date(b).getTime() ? 1 : -1;
    });
};

export function findEarliestDate(dates, eliminerDatesPassees = false) {

  if (!Array.isArray(dates) || dates.length === 0) {
    return null; // Return null for invalid input or an empty list
  }

  let list = [...dates.map(el=>gDate(el))];

  // Si l'option d'élimination des dates passées est activée
  if (eliminerDatesPassees) {
    const dateActuelle = new Date();
    list = list.filter(date => date >= dateActuelle);
  }

  // Triez les dates par ordre croissant
  list.sort((a, b) => a - b);

  // La première date dans le tableau trié est la date la plus proche
  if (dates.length > 0) {
    return dates[0];
  }

  // Si aucune date valide n'a été trouvée
  return null;
}
